import {
    Home,
    FileText,
    CreditCard,
    DollarSign,
    Folder,
    CheckCircle,
    Send,
    Building2,
    ClipboardList,
    MessageSquare,
    FileSignature,
    RotateCcw,
    FileEdit,
    Receipt,
    HelpCircle,
    Newspaper,
    TicketSlashIcon
} from "lucide-react";
import { PERMISSIONS } from "../../constants/permissions";
import { hasFulfilledClosingFeeOrder } from "../../services/orderService";
import { shouldShowOrderSmartFeesForBusinessMenu, parseBool } from "../../utils/closingFeeUtils";
import ConfigDetails from "../../config_variables/config_resolver";

const creditExternalPageID = process.env.REACT_APP_CUSTOMCREDIT_EXTERNAL;
const commercialFormExternalPageID = process.env.REACT_APP_COMMERCIALFORM_EXTERNAL;
const newCommentExternalPageID = process.env.REACT_APP_NEWCOMMENT_EXTERNAL;
const preSubmissionFormExternalID = process.env.REACT_APP_PRESUBMISSION_FORM;
const disclosuresValidationExternalPageID = process.env.REACT_APP_VALIDATION_DISCLOSURE_EXTERNAL;
const submitLoanValidationExternalPageID = process.env.REACT_APP_VALIDATION_SUBMITLOAN_EXTERNAL;
const isCOCVisible = process.env.REACT_APP_CHANGE_OF_CIRCUMSTANCE === "true";
const ContactSupportExternalPageID = process.env.REACT_APP_CONTACT_SUPPORT_EXTERNAL;
const pricingValidationExternalPageID = process.env.REACT_APP_VALIDATION_PRICING_EXTERNAL;
const creditValidationExternalPageID = ConfigDetails.REACT_APP_VALIDATION_CREDIT_EXTERNAL;
const creditValidationConfig = { validationExtPageId: creditValidationExternalPageID, validationPage: "OrderCreditValidation" };
const pricingBetaExternalPageID = ConfigDetails.REACT_APP_PRICING_BETA_EXTERNAL_PAGEID;
const feeValidationExternalPageID = process.env.REACT_APP_VALIDATION_FEE_EXTERNAL;
const ChangeOfCircumstance = process.env.REACT_APP_COC_EXTERNAL;
const requestDisclosureValidationExternalPageID = process.env.REACT_APP_VALIDATION_REQUEST_DISCLOSURE_EXTERNAL;

const cashToCloseExternalPageID = process.env.REACT_APP_CASH_TO_CLOSE_EXTERNAL;
const feeBreakdownExternalPageID = process.env.REACT_APP_FEE_BREAKDOWN_EXTERNAL;

const feeValidationAppraisalPageID = process.env.REACT_APP_VALIDATION_APPRAISAL_EXTERNAL;

const consumerAdditionalInfoExternalPageID = process.env.REACT_APP_CONSUMER_ADDITIONALINFO_EXTERNAL;

const CorrespondentNonDelegateAddInfo = process.env.REACT_APP_NONDELEGATE_ADDINFO;
const CorrespondentNonDelegateContact = process.env.REACT_APP_NONDELEGATE_CONTACT;
const CorrespondentNonDelegateConversation = process.env.REACT_APP_NONDELEGATE_CONVERSATION;
const CorrespondentNonDelegateGuidelineException = process.env.REACT_APP_NONDELEGATE_GUIDELINEEXCPTION;

const CorrespondentDelegateAddInfo = process.env.REACT_APP_DELEGATE_ADDINFO;
const CorrespondentDelegateContact = process.env.REACT_APP_DELEGATE_CONTACT;
const CorrespondentDelegateConversation = process.env.REACT_APP_DELEGATE_CONVERSATION;
const CorrespondenDelegateGuidelineException = process.env.REACT_APP_DELEGATE_GUIDELINEEXCPTION;

const cashtoCloseDelegateExternalPageID = process.env.REACT_APP_CASH_TO_CLOSE_DELEGATE;
const cashtoCloseNonDelegateExternalPageID = process.env.REACT_APP_CASH_TO_CLOSE_NONDELEGATE;
const commercialFormDelegateExternalPageID = process.env.REACT_APP_COMMERCIALFORM_DELEGATE;
const commercialFormNonDelegateExternalPageID = process.env.REACT_APP_COMMERCIALFORM_NONDELEGATE;

const businessAddInfoDelegateExternalPageID = process.env.REACT_APP_BUSINESS_ADDINFO_DELEGATE;
const businessAddInfoNonDelegateExternalPageID = process.env.REACT_APP_BUSINESS_ADDINFO_NONDELEGATE;

const NEW_TPOPAGELINK_NATIVEPAGENAME = parseBool(process.env?.REACT_APP_ENABLE_NEW_TPOPAGELINK);
const DOCUMENTS_MENU_LABEL = NEW_TPOPAGELINK_NATIVEPAGENAME ? "eFolder" : "documents";
const isBusinessUrlaContinuationVisible = parseBool(process.env.REACT_APP_ENABLE_BUSINESS_URLA_CONTINUATION);

const isBetaConditionVsisible = process.env.REACT_APP_ENABLE_CONDITIONBETA_PAGE === "true";
const conditionWsExternalID = ConfigDetails.REACT_APP_CONDITION_WS_EXTERNALID;
const conditionCordelExternalID = ConfigDetails.REACT_APP_CONDITION_CORDEL_EXTERNALID;
const conditionCornondelExternalID = ConfigDetails.REACT_APP_CONDITION_CORNONDEL_EXTERNALID;

const isBetaDocumentsVisible = process.env.REACT_APP_ENABLE_DOCUMENTSBETA_PAGE === "true";
const isPricingBetaMenuVisible = parseBool(ConfigDetails.REACT_APP_ENABLE_PRICING_BETA_MENU);
const documentsWsExternalID = ConfigDetails.REACT_APP_DOCUMENTS_WS_EXTERNALID;
const documentsCordelExternalID = ConfigDetails.REACT_APP_DOCUMENTS_CORDEL_EXTERNALID;
const documentsCornondelExternalID = ConfigDetails.REACT_APP_DOCUMENTS_CORNONDEL_EXTERNALID;

const cancelLoanExternalID = process.env.REACT_APP_CANCEL_LOAN_EXTERNALID;

const guidelineExceptionValidationExternalPageID = process.env.REACT_APP_VALIDATION_GUIDELINE_EXCEPTION_EXTERNAL;
const aiDocumentReviewExternalPageID = ConfigDetails.REACT_APP_AIDOCUMENT_REVIEW;

export const isAiDocumentReviewMenuCompleted = (_loanFields, extraContext = {}) => {
    if (!extraContext?.aiDocReviewStatusLoaded) {
        return false;
    }

    const pendingCount = Number(extraContext?.aiDocReviewPendingCount);
    if (!Number.isFinite(pendingCount)) {
        return false;
    }

    return pendingCount <= 0;
};

const appraisalOrderInfoExternalPageID = process.env.REACT_APP_APPRAISAL_ORDER_INFO_EXTERNAL;

const getBorrowerPairCount = loanFields => {
    const raw = loanFields?.["4460"];
    const count = parseInt(raw, 10);

    if (!Number.isFinite(count) || count <= 0) return 1;

    // for now support max 5 pairs
    return Math.min(count, 5);
};

export const isCreditMenuCompleted = loanFields => {
    const borrowerPairCount = getBorrowerPairCount(loanFields);

    for (let i = 1; i <= borrowerPairCount; i++) {
        const value = loanFields?.[`300#${i}`];

        if (isEmptyField(value)) {
            return false;
        }
    }

    return true;
};

export const isOrderSmartFeesMenuCompleted = (loanFields, extraContext = {}) => {
    const serviceOrders = extraContext?.serviceOrders || [];
    return hasFulfilledClosingFeeOrder(serviceOrders);
};

const orderSmartFeesMenuItem = {
    label: "Service Orders",
    displayName: "Order Smart Fees",
    icon: ClipboardList,
    validationExtPageId: feeValidationExternalPageID,
    validationPage: "FeeValidation",
    isExcludeFromSequence: false,
    isMenuCompleted: isOrderSmartFeesMenuCompleted
};

const pricingBetaMenuItem = {
    label: "Pricing (Beta)",
    displayName: "Pricing (Beta)",
    icon: DollarSign,
    extPageId: pricingBetaExternalPageID,
    showIf: "isPricingBetaMenuVisible",
    isExcludeFromSequence: false,
    isMenuCompleted: ["1401"],
    requiredPermission: PERMISSIONS.VIEW_PRICING
};

export const menuItemsCP = [
    { label: "Loan Summary", displayName: "Loan Summary", icon: Home, isDefault: true, isExcludeFromSequence: false },
    {
        label: "URLA:Additional Information",
        displayName: "URLA 2020",
        icon: FileText,
        subsections: [
            { label: "CustomAdditionalInformation", displayName: "Additional Information", extPageId: consumerAdditionalInfoExternalPageID },
            { label: "URLA:Lender Loan Information", displayName: "Lender Loan Info" },
            { label: "URLA:Borrower Information", displayName: "Borrower Information" },
            { label: "URLA:Employment and Income", displayName: "Employment & Income" },
            { label: "URLA:Assets and Liabilities", displayName: "Assets and Liabilities" },
            { label: "URLA:Real Estate", displayName: "Real Estate" },
            { label: "URLA:Loan and Property Information", displayName: "Loan & Property" },
            { label: "URLA:Information for Government Monitoring", displayName: "Government Monitoring" },
            { label: "URLA:URLA Continuation", displayName: "Continuation" }
        ],
        isExcludeFromSequence: false,
        isMenuCompleted: [
            "CX.LOAN.PROD.BROKER.SELECT",
            "CX.DOC.TYPE",
            "1548",
            "LCP.X1",
            "CX.CITIZENSHIP.TYPE",
            "1041",
            "16",
            "CX.INCOME.TYPE",
            "URLA.X73",
            "12",
            "14",
            "15",
            "1821",
            "1041",
            "1811",
            "608",
            "4494",
            "1109",
            "4000",
            "4002",
            "65",
            "1402",
            "URLA.X1",
            "52",
            "1490",
            "1240",
            "BR0126",
            "BR0106",
            "BR0107",
            "BR0108",
            "BR0112",
            "BR0124",
            "FR0126",
            "FR0106",
            "FR0107",
            "FR0108"
        ]
    },
    {
        label: "DUAL AUS",
        displayName: "Order/ Reissue Credit",
        icon: CreditCard,
        ...creditValidationConfig,
        isMenuCompleted: isCreditMenuCompleted,
        isExcludeFromSequence: false
    },
    {
        label: "Pricing",
        displayName: "Pricing",
        icon: FileText,
        isMenuCompleted: ["1401"],
        isExcludeFromSequence: false,
        requiredPermission: PERMISSIONS.VIEW_PRICING,
        subsections: [
            {
                label: "Product Pricing & Lock",
                displayName: "Product Pricing & Lock",
                icon: DollarSign,
                validationExtPageId: pricingValidationExternalPageID,
                validationPage: "PricingValidation"
            },
            {
                label: "Request Guideline Exception",
                displayName: "Request Guideline Exception",
                icon: MessageSquare,
                extPageId: preSubmissionFormExternalID,
                validationExtPageId: guidelineExceptionValidationExternalPageID,
                validationPage: "GuidelineExceptionValidation",
                fieldId: "LOG.MS.DATE.SUBMITTED TO UW",
                showIf: "isGuidelineExceptionVisible"
            }
        ]
    },
    pricingBetaMenuItem,
    { label: DOCUMENTS_MENU_LABEL, displayName: "Documents", icon: Folder, isMenuCompleted: [], isExcludeFromSequence: false, requiredPermission: PERMISSIONS.UPLOAD_FILES },
    {
        label: "Documents Beta",
        displayName: "Documents (Beta)",
        icon: Folder,
        showIf: "isWsDocumentsBetaVisible",
        isExcludeFromSequence: false,
        isMenuCompleted: [],
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        extPageId: documentsWsExternalID
    },
    orderSmartFeesMenuItem,
    {
        label: "Loan Estimate Fee Management",
        displayName: "LE Fee Management",
        icon: Receipt,
        showIf: "isRequestLE",
        isMenuCompleted: ["TPO.X115"],
        isExcludeFromSequence: false
    },
    {
        label: "Send Disclosures",
        displayName: loanFields => {
            return shouldShowViewDisclosures(loanFields) ? "View Disclosures" : "Send Disclosures";
        },
        icon: FileSignature,
        validationExtPageId: disclosuresValidationExternalPageID,
        validationPage: "DisclosuresValidation",
        requiredFieldId: "1401",
        fieldId: "LE1.X33",
        showIf: "showSendDisclosures",
        isMenuCompleted: loanFields => shouldShowViewDisclosures(loanFields),
        isExcludeFromSequence: false
    },
    {
        label: "Disclosures",
        displayName: "Request Disclosure",
        icon: Receipt,
        showIf: "isDisclosure",
        validationExtPageId: requestDisclosureValidationExternalPageID,
        validationPage: "RequestDisclosuresValidation",
        isMenuCompleted: ["TPO.X92"],
        isExcludeFromSequence: false
    },
    {
        label: "Submit Loan",
        displayName: "Submit Loan",
        icon: Send,
        fieldId: "TPO.X90",
        showIf: "isInitialSubmit",
        validationExtPageId: submitLoanValidationExternalPageID,
        validationPage: "SubmitLoanValidation",
        isMenuCompleted: ["TPO.X90"],
        isExcludeFromSequence: false
    },
    { label: "Cancel Loan", displayName: "Cancel Loan", isExcludeFromSequence: false, showIf: "isCancelLoan", extPageId: cancelLoanExternalID },
    {
        label: "Enhanced Conditions",
        displayName: "Conditions",
        icon: CheckCircle,
        showIf: "isSubmittoUW",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        }
    },
    {
        label: "Conditions Beta",
        displayName: "Conditions (Beta)",
        icon: CheckCircle,
        showIf: "isWsConditionsBetaVisible",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        },
        subsections: [
            {
                label: "Upload Conditions",
                displayName: "Upload Conditions",
                icon: CheckCircle,
                showIf: "isWsConditionsBetaVisible",
                //isExcludeFromSequence: true,
                extPageId: conditionWsExternalID
            },
            {
                label: "AI Document Review",
                displayName: "Document Index",
                icon: Folder,
                showIf: "isAiDocumentReviewVisible",
                //isExcludeFromSequence: true,
                //isMenuCompleted: isAiDocumentReviewMenuCompleted,
                extPageId: aiDocumentReviewExternalPageID
            }
        ]
    },
    // { label: 'Re-Submit Loan', displayName: 'Re-Submit Loan', icon: RotateCcw, fieldId: 'TPO.X4', isExcludeFromSequence: false, showIf: 'isReSubmit', isMenuCompleted: ["TPO.X4"] },
    {
        label: "AppraisalOrderInformation",
        displayName: "Appraisal Order Information",
        icon: ClipboardList,
        extPageId: appraisalOrderInfoExternalPageID,
        //validationExtPageId: feeValidationAppraisalPageID,
        //validationPage: "AppraisalValidation",
        //isMenuCompleted: ["1041", "2353", "2352"],
        showIf: "isAppraisalOrderInfoVisible",
        isExcludeFromSequence: true
    },
    {
        label: "Service Orders",
        displayName: "Order Appraisal",
        icon: ClipboardList,
        validationExtPageId: feeValidationAppraisalPageID,
        validationPage: "AppraisalValidation",
        isMenuCompleted: ["1041", "2353", "2352"],
        isExcludeFromSequence: true
    },
    {
        label: "Change of circumstance",
        displayName: "Change Request",
        icon: FileEdit,
        showIf: "isChangeOfCircumstance",
        fieldId: "TPO.X90",
        extPageId: ChangeOfCircumstance,
        isMenuCompleted: ["CX.COC.CHANGEDATE"],
        isExcludeFromSequence: true
    },
    // {
    //     label: 'LoanComment',
    //     displayName: 'Conversation Log',
    //     icon: MessageSquare,
    //     extPageId: newCommentExternalPageID,
    //     showCount: {
    //         fieldId: 'CX.BROK.UNREADMSG.COUNT', // Field to check for count
    //         hideOnNavigate: true // Hide count when navigating to this page
    //     },
    //     isExcludeFromSequence: true
    // },
    { label: "ContactSupport", displayName: "Contact Support", icon: HelpCircle, extPageId: ContactSupportExternalPageID, isExcludeFromSequence: true }
];

export const menuItemsBP = [
    { label: "Loan Summary", displayName: "Loan Summary", icon: Home, isDefault: true, isExcludeFromSequence: false },
    {
        label: "CommercialApp",
        displayName: "Commercial Form",
        icon: Building2,
        subsections: [
            {
                label: "CommercialForm",
                displayName: "Commercial Loan Application",
                extPageId: commercialFormExternalPageID
            },
            {
                label: "CashtoClose",
                displayName: "Cash-to-Close",
                extPageId: cashToCloseExternalPageID
            },
            {
                label: "URLA:Assets and Liabilities",
                displayName: "Assets and Liabilities"
            },
            ...(isBusinessUrlaContinuationVisible ? [{ label: "URLA:URLA Continuation", displayName: "Continuation" }] : [])
        ],
        isMenuCompleted: ["CX.LOAN.PROD.BROKER.SELECT", "1548", "389", "1041", "1548", "CX.DOC.TYPE", "CX.INCOME.TYPE", "URLA.X73"],
        isExcludeFromSequence: false
    },
    {
        label: "CorelogicCredco",
        displayName: "Order/ Reissue Credit",
        icon: CreditCard,
        extPageId: creditExternalPageID,
        ...creditValidationConfig,
        isMenuCompleted: isCreditMenuCompleted,
        isExcludeFromSequence: false
    },
    {
        label: "Pricing",
        displayName: "Pricing",
        icon: FileText,
        isExcludeFromSequence: false,
        isMenuCompleted: ["1401"],
        requiredPermission: PERMISSIONS.VIEW_PRICING,
        subsections: [
            {
                label: "Product Pricing & Lock",
                displayName: "Product Pricing & Lock",
                icon: DollarSign,
                validationExtPageId: pricingValidationExternalPageID,
                validationPage: "PricingValidation"
            },
            {
                label: "Request Guideline Exception",
                displayName: "Request Guideline Exception",
                icon: MessageSquare,
                extPageId: preSubmissionFormExternalID,
                validationExtPageId: guidelineExceptionValidationExternalPageID,
                validationPage: "GuidelineExceptionValidation",
                fieldId: "LOG.MS.DATE.SUBMITTED TO UW",
                showIf: "isGuidelineExceptionVisible"
            }
        ]
    },
    pricingBetaMenuItem,
    { label: DOCUMENTS_MENU_LABEL, displayName: "Documents", icon: Folder, isMenuCompleted: [], isExcludeFromSequence: false, requiredPermission: PERMISSIONS.UPLOAD_FILES },
    {
        label: "Documents Beta",
        displayName: "Documents (Beta)",
        icon: Folder,
        showIf: "isWsDocumentsBetaVisible",
        isExcludeFromSequence: false,
        isMenuCompleted: [],
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        extPageId: documentsWsExternalID
    },
    ...(shouldShowOrderSmartFeesForBusinessMenu() ? [orderSmartFeesMenuItem] : []),
    // { label: 'Loan Estimate Fee Management', displayName: 'Loan Estimate Fee Management', icon: Receipt, showIf: "isRequestLE" },
    {
        label: "Send Disclosures",
        displayName: loanFields => {
            return shouldShowViewDisclosures(loanFields) ? "View Disclosures" : "Send Disclosures";
        },
        icon: FileSignature,
        validationExtPageId: disclosuresValidationExternalPageID,
        validationPage: "DisclosuresValidation",
        requiredFieldId: "1401",
        fieldId: "LE1.X33",
        showIf: "showSendDisclosures",
        isMenuCompleted: loanFields => shouldShowViewDisclosures(loanFields),

        isExcludeFromSequence: false
    },
    {
        label: "Disclosures",
        displayName: "Request Disclosure",
        icon: Receipt,
        showIf: "isDisclosure",
        validationExtPageId: requestDisclosureValidationExternalPageID,
        validationPage: "RequestDisclosuresValidation",
        isMenuCompleted: ["TPO.X92"],
        isExcludeFromSequence: false
    },
    {
        label: "Submit Loan",
        displayName: "Submit Loan",
        icon: Send,
        fieldId: "TPO.X90",
        showIf: "isInitialSubmitBP",
        validationExtPageId: submitLoanValidationExternalPageID,
        validationPage: "SubmitLoanValidation",
        isMenuCompleted: ["TPO.X90"],
        isExcludeFromSequence: false
    },
    { label: "Cancel Loan", displayName: "Cancel Loan", isExcludeFromSequence: false, showIf: "isCancelLoan", extPageId: cancelLoanExternalID },
    {
        label: "FeeBreakDown",
        displayName: "Fee Break-down",
        icon: Receipt,
        isExcludeFromSequence: true,
        extPageId: feeBreakdownExternalPageID
    },
    {
        label: "Enhanced Conditions",
        displayName: "Conditions",
        icon: CheckCircle,
        showIf: "isSubmittoUW",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        }
    },
    {
        label: "Conditions Beta",
        displayName: "Conditions (Beta)",
        icon: CheckCircle,
        showIf: "isWsConditionsBetaVisible",
        isExcludeFromSequence: true,
        //isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        },
        subsections: [
            {
                label: "Upload Conditions",
                displayName: "Upload Conditions",
                icon: CheckCircle,
                showIf: "isWsConditionsBetaVisible",

                extPageId: conditionWsExternalID
            },
            {
                label: "AI Document Review",
                displayName: "Document Index",
                icon: Folder,
                showIf: "isAiDocumentReviewVisible",
                //isExcludeFromSequence: true,
                //isMenuCompleted: isAiDocumentReviewMenuCompleted,
                extPageId: aiDocumentReviewExternalPageID
            }
        ]
    },
    {
        label: "AppraisalOrderInformation",
        displayName: "Appraisal Order Information",
        icon: ClipboardList,
        extPageId: appraisalOrderInfoExternalPageID,
        showIf: "isAppraisalOrderInfoVisible",
        isExcludeFromSequence: true
    },
    // { label: 'Re-Submit Loan', displayName: 'Re-Submit Loan', icon: RotateCcw, fieldId: 'TPO.X4', showIf: 'isReSubmit', isExcludeFromSequence: false, isMenuCompleted: ["TPO.X4"] },
    { label: "Service Orders", displayName: "Order Appraisal", icon: ClipboardList, isMenuCompleted: ["1041", "2353", "2352"], isExcludeFromSequence: true },
    {
        label: "Change of circumstance",
        displayName: "Change Request",
        icon: FileEdit,
        showIf: "isChangeOfCircumstance",
        fieldId: "TPO.X90",
        extPageId: ChangeOfCircumstance,
        isMenuCompleted: ["CX.COC.CHANGEDATE"],
        isExcludeFromSequence: true
    },
    // {
    //     label: 'LoanComment',
    //     displayName: 'Conversation Log',
    //     icon: MessageSquare,
    //     extPageId: newCommentExternalPageID,
    //     showCount: {
    //         fieldId: 'CX.BROK.UNREADMSG.COUNT', // Field to check for count
    //         hideOnNavigate: true // Hide count when navigating to this page
    //     }, isExcludeFromSequence: true
    // },
    { label: "ContactSupport", displayName: "Contact Support", icon: HelpCircle, extPageId: ContactSupportExternalPageID, isExcludeFromSequence: true }
];

export const menuItemsDelegate = [
    { label: "Loan Summary", displayName: "Loan Summary", icon: Home, isDefault: true, isExcludeFromSequence: false },
    {
        label: "URLA:Additional Information",
        displayName: "URLA 2020",
        icon: FileText,
        subsections: [
            { label: "URLA:Lender Loan Information", displayName: "Lender Loan Info" },
            { label: "URLA:Borrower Information", displayName: "Borrower Information" },
            { label: "URLA:Employment and Income", displayName: "Employment & Income" },
            { label: "URLA:Assets and Liabilities", displayName: "Assets and Liabilities" },
            { label: "URLA:Real Estate", displayName: "Real Estate" },
            { label: "URLA:Loan and Property Information", displayName: "Loan & Property" },
            { label: "URLA:Information for Government Monitoring", displayName: "Government Monitoring" },
            { label: "URLA:URLA Continuation", displayName: "Continuation" }
        ],
        isExcludeFromSequence: false,
        isMenuCompleted: [
            "URLA.X73",
            "12",
            "14",
            "15",
            "1811",
            "608",
            "4494",
            "1109",
            "4000",
            "4002",
            "65",
            "1402",
            "URLA.X1",
            "52",
            "1490",
            "1240",
            "BR0126",
            "BR0106",
            "BR0107",
            "BR0108",
            "BR0112",
            "BR0124",
            "FR0126",
            "FR0106",
            "FR0107",
            "FR0108"
        ]
    },
    {
        label: "CustomAdditionalInformation",
        displayName: "Additional Required Information",
        extPageId: CorrespondentDelegateAddInfo,
        isExcludeFromSequence: false,
        isMenuCompleted: ["CX.LOAN.PROD.BROKER.SELECT", "CX.DOC.TYPE", "1548", "LCP.X1", "CX.CITIZENSHIP.TYPE", "1041", "16", "CX.INCOME.TYPE", "1821"]
    },
    {
        label: "Product Pricing & Lock",
        displayName: "Pricing",
        icon: FileText,
        isMenuCompleted: ["1401"],
        isExcludeFromSequence: false,
        validationExtPageId: pricingValidationExternalPageID,
        validationPage: "PricingValidation",
        requiredPermission: PERMISSIONS.VIEW_PRICING
    },
    // pricingBetaMenuItem,
    {
        label: DOCUMENTS_MENU_LABEL,
        displayName: "Documents",
        icon: Folder,
        isMenuCompleted: [],
        isExcludeFromSequence: false,
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        showIf: "isRegistrationCompletedAndNotClosedLoanSubmitted"
    },
    {
        label: "Documents Beta",
        displayName: "Documents (Beta)",
        icon: Folder,
        showIf: "isCorDelDocumentsBetaVisible",
        isExcludeFromSequence: false,
        isMenuCompleted: [],
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        extPageId: documentsCordelExternalID
    },
    {
        label: "Submit for Review",
        displayName: "Submit for Delegated Review",
        icon: Send,
        isExcludeFromSequence: false,
        isMenuCompleted: ["TPO.X90"],
        showIf: "isRegistrationCompleted"
    },
    // {
    //     label: 'LoanComment',
    //     displayName: 'Conversation Log',
    //     icon: MessageSquare,
    //     extPageId: CorrespondentDelegateConversation,
    //     showCount: {
    //         fieldId: 'CX.BROK.UNREADMSG.COUNT', // Field to check for count
    //         hideOnNavigate: true // Hide count when navigating to this page
    //     },
    //     isExcludeFromSequence: true
    // },
    {
        label: "Enhanced Conditions",
        displayName: "Conditions",
        icon: CheckCircle,
        showIf: "isDelegateConditionsVisible",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        }
    },
    {
        label: "Conditions Beta",
        displayName: "Conditions (Beta)",
        icon: CheckCircle,
        showIf: "isCorDelConditionsBetaVisible",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        },
        extPageId: conditionCordelExternalID
    },
    { label: "ContactSupport", displayName: "Contact Support", icon: HelpCircle, extPageId: CorrespondentDelegateContact, isExcludeFromSequence: true },
    {
        label: "Request Guideline Exception",
        displayName: "Request Guideline Exception",
        icon: TicketSlashIcon,
        extPageId: CorrespondenDelegateGuidelineException,
        fieldId: "Log.MS.Date.In Purchase Review",
        showIf: "isDelGuidelineExceptionVisible",
        isExcludeFromSequence: true
    },
    {
        label: "Purchase Advice",
        displayName: "Purchase Advice",
        icon: Newspaper,
        isExcludeFromSequence: true,
        showIf: "purchaseAdviceIsVisible",
        requiredPermission: PERMISSIONS.VIEW_PURCHASE_ADVICE
    }
];

export const menuItemsNonDelegate = [
    { label: "Loan Summary", displayName: "Loan Summary", icon: Home, isDefault: true, isExcludeFromSequence: false },
    {
        label: "URLA:Additional Information",
        displayName: "URLA 2020",
        icon: FileText,
        subsections: [
            { label: "URLA:Lender Loan Information", displayName: "Lender Loan Info" },
            { label: "URLA:Borrower Information", displayName: "Borrower Information" },
            { label: "URLA:Employment and Income", displayName: "Employment & Income" },
            { label: "URLA:Assets and Liabilities", displayName: "Assets and Liabilities" },
            { label: "URLA:Real Estate", displayName: "Real Estate" },
            { label: "URLA:Loan and Property Information", displayName: "Loan & Property" },
            { label: "URLA:Information for Government Monitoring", displayName: "Government Monitoring" },
            { label: "URLA:URLA Continuation", displayName: "Continuation" }
        ],
        isExcludeFromSequence: false,
        isMenuCompleted: [
            "URLA.X73",
            "12",
            "14",
            "15",
            "1811",
            "608",
            "4494",
            "1109",
            "4000",
            "4002",
            "65",
            "1402",
            "URLA.X1",
            "52",
            "1490",
            "1240",
            "BR0126",
            "BR0106",
            "BR0107",
            "BR0108",
            "BR0112",
            "BR0124",
            "FR0126",
            "FR0106",
            "FR0107",
            "FR0108"
        ]
    },
    {
        label: "CustomAdditionalInformation",
        displayName: "Additional Required Information",
        extPageId: CorrespondentNonDelegateAddInfo,
        isExcludeFromSequence: false,
        isMenuCompleted: ["CX.LOAN.PROD.BROKER.SELECT", "CX.DOC.TYPE", "1548", "LCP.X1", "CX.CITIZENSHIP.TYPE", "1041", "16", "CX.INCOME.TYPE", "1821"]
    },
    {
        label: "Product Pricing & Lock",
        displayName: "Pricing",
        icon: FileText,
        isMenuCompleted: ["1401"],
        isExcludeFromSequence: false,
        validationExtPageId: pricingValidationExternalPageID,
        validationPage: "PricingValidation",
        requiredPermission: PERMISSIONS.VIEW_PRICING
    },
    //pricingBetaMenuItem,
    {
        label: DOCUMENTS_MENU_LABEL,
        displayName: "Documents",
        icon: Folder,
        isMenuCompleted: [],
        isExcludeFromSequence: false,
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        showIf: "isRegistrationCompletedAndNotSubmittedtoUW"
    },
    {
        label: "Documents Beta",
        displayName: "Documents (Beta)",
        icon: Folder,
        showIf: "isCorNonDelDocumentsBetaVisible",
        isExcludeFromSequence: false,
        isMenuCompleted: [],
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        extPageId: documentsCornondelExternalID
    },
    {
        label: "Submit for Non-Delegated Review",
        displayName: "Submit for Non-Delegated Review",
        icon: Send,
        isExcludeFromSequence: false,
        isMenuCompleted: ["TPO.X90"],
        showIf: "isRegistrationCompleted"
    },
    // {
    //     label: 'LoanComment',
    //     displayName: 'Conversation Log',
    //     icon: MessageSquare,
    //     extPageId: CorrespondentNonDelegateConversation,
    //     showCount: {
    //         fieldId: 'CX.BROK.UNREADMSG.COUNT', // Field to check for count
    //         hideOnNavigate: true // Hide count when navigating to this page
    //     },
    //     isExcludeFromSequence: true
    // },
    {
        label: "Enhanced Conditions",
        displayName: "Conditions",
        icon: CheckCircle,
        showIf: "isNonDelegatedClosedFiles",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        }
    },
    {
        label: "Conditions Beta",
        displayName: "Conditions (Beta)",
        icon: CheckCircle,
        showIf: "isCorNonDelConditionsBetaVisible",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        },
        extPageId: conditionCornondelExternalID
    },
    { label: "ContactSupport", displayName: "Contact Support", icon: HelpCircle, extPageId: CorrespondentNonDelegateContact, isExcludeFromSequence: true },
    {
        label: "Request Guideline Exception",
        displayName: "Request Guideline Exception",
        icon: TicketSlashIcon,
        extPageId: CorrespondentNonDelegateGuidelineException,
        fieldId: "LOG.MS.DATE.SUBMITTED TO UW",
        showIf: "isGuidelineExceptionVisible",
        isExcludeFromSequence: true
    },
    {
        label: "Purchase Advice",
        displayName: "Purchase Advice",
        icon: Newspaper,
        isExcludeFromSequence: true,
        showIf: "purchaseAdviceIsVisible",
        requiredPermission: PERMISSIONS.VIEW_PURCHASE_ADVICE
    }
];

export const menuItemsDelegateBP = [
    { label: "Loan Summary", displayName: "Loan Summary", icon: Home, isDefault: true, isExcludeFromSequence: false },
    {
        label: "CommercialApp",
        displayName: "Commercial Form",
        icon: Building2,
        subsections: [
            {
                label: "CommercialForm",
                displayName: "Commercial Loan Application",
                extPageId: commercialFormDelegateExternalPageID
            },
            {
                label: "CashtoClose",
                displayName: "Cash-to-Close",
                extPageId: cashtoCloseDelegateExternalPageID
            },
            {
                label: "URLA:Assets and Liabilities",
                displayName: "Assets and Liabilities"
            }
        ],
        isMenuCompleted: ["1041", "URLA.X73", "CX.CITIZENSHIP.TYPE"],
        isExcludeFromSequence: false
    },
    {
        label: "BusinessCustomAdditionalInformation",
        displayName: "Additional Required Information",
        extPageId: businessAddInfoDelegateExternalPageID,
        isExcludeFromSequence: false,
        isMenuCompleted: ["CX.LOAN.PROD.BROKER.SELECT", "CX.DOC.TYPE", "1548", "LCP.X1", "16", "CX.INCOME.TYPE", "1821"]
    },
    {
        label: "Product Pricing & Lock",
        displayName: "Pricing",
        icon: FileText,
        isMenuCompleted: ["1401"],
        isExcludeFromSequence: false,
        validationExtPageId: pricingValidationExternalPageID,
        validationPage: "PricingValidation",
        requiredPermission: PERMISSIONS.VIEW_PRICING
    },
    //pricingBetaMenuItem,
    {
        label: DOCUMENTS_MENU_LABEL,
        displayName: "Documents",
        icon: Folder,
        isMenuCompleted: [],
        isExcludeFromSequence: false,
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        showIf: "isRegistrationCompletedAndNotClosedLoanSubmitted"
    },
    {
        label: "Documents Beta",
        displayName: "Documents (Beta)",
        icon: Folder,
        showIf: "isCorDelDocumentsBetaVisible",
        isExcludeFromSequence: false,
        isMenuCompleted: [],
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        extPageId: documentsCordelExternalID
    },
    {
        label: "Submit for Review",
        displayName: "Submit for Delegated Review",
        icon: Send,
        isExcludeFromSequence: false,
        isMenuCompleted: ["TPO.X90"],
        showIf: "isRegistrationCompleted"
    },
    {
        label: "Enhanced Conditions",
        displayName: "Conditions",
        icon: CheckCircle,
        showIf: "isDelegateConditionsVisible",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        }
    },
    {
        label: "Conditions Beta",
        displayName: "Conditions (Beta)",
        icon: CheckCircle,
        showIf: "isCorDelConditionsBetaVisible",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        },
        extPageId: conditionCordelExternalID
    },
    { label: "ContactSupport", displayName: "Contact Support", icon: HelpCircle, extPageId: CorrespondentDelegateContact, isExcludeFromSequence: true },
    {
        label: "Request Guideline Exception",
        displayName: "Request Guideline Exception",
        icon: TicketSlashIcon,
        extPageId: CorrespondenDelegateGuidelineException,
        fieldId: "Log.MS.Date.In Purchase Review",
        showIf: "isDelGuidelineExceptionVisible",
        isExcludeFromSequence: true
    },
    {
        label: "Purchase Advice",
        displayName: "Purchase Advice",
        icon: Newspaper,
        isExcludeFromSequence: true,
        showIf: "purchaseAdviceIsVisible",
        requiredPermission: PERMISSIONS.VIEW_PURCHASE_ADVICE
    }
];

export const menuItemsNonDelegateBP = [
    { label: "Loan Summary", displayName: "Loan Summary", icon: Home, isDefault: true, isExcludeFromSequence: false },
    {
        label: "CommercialApp",
        displayName: "Commercial Form",
        icon: Building2,
        subsections: [
            {
                label: "CommercialForm",
                displayName: "Commercial Loan Application",
                extPageId: commercialFormNonDelegateExternalPageID
            },
            {
                label: "CashtoClose",
                displayName: "Cash-to-Close",
                extPageId: cashtoCloseNonDelegateExternalPageID
            },
            {
                label: "URLA:Assets and Liabilities",
                displayName: "Assets and Liabilities"
            }
        ],
        isMenuCompleted: ["1041", "URLA.X73", "CX.CITIZENSHIP.TYPE"],
        isExcludeFromSequence: false
    },
    {
        label: "BusinessCustomAdditionalInformation",
        displayName: "Additional Required Information",
        extPageId: businessAddInfoNonDelegateExternalPageID,
        isExcludeFromSequence: false,
        isMenuCompleted: ["CX.LOAN.PROD.BROKER.SELECT", "CX.DOC.TYPE", "1548", "LCP.X1", "16", "CX.INCOME.TYPE", "1821"]
    },
    {
        label: "Product Pricing & Lock",
        displayName: "Pricing",
        icon: FileText,
        isMenuCompleted: ["1401"],
        isExcludeFromSequence: false,
        validationExtPageId: pricingValidationExternalPageID,
        validationPage: "PricingValidation",
        requiredPermission: PERMISSIONS.VIEW_PRICING
    },
    //pricingBetaMenuItem,
    {
        label: DOCUMENTS_MENU_LABEL,
        displayName: "Documents",
        icon: Folder,
        isMenuCompleted: [],
        isExcludeFromSequence: false,
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        showIf: "isRegistrationCompletedAndNotSubmittedtoUW"
    },
    {
        label: "Documents Beta",
        displayName: "Documents (Beta)",
        icon: Folder,
        showIf: "isCorNonDelDocumentsBetaVisible",
        isExcludeFromSequence: false,
        isMenuCompleted: [],
        requiredPermission: PERMISSIONS.UPLOAD_FILES,
        extPageId: documentsCornondelExternalID
    },
    {
        label: "Submit for Non-Delegated Review",
        displayName: "Submit for Non-Delegated Review",
        icon: Send,
        isExcludeFromSequence: false,
        isMenuCompleted: ["TPO.X90"],
        showIf: "isRegistrationCompleted"
    },
    {
        label: "Enhanced Conditions",
        displayName: "Conditions",
        icon: CheckCircle,
        showIf: "isNonDelegatedClosedFiles",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        }
    },
    {
        label: "Conditions Beta",
        displayName: "Conditions (Beta)",
        icon: CheckCircle,
        showIf: "isCorNonDelConditionsBetaVisible",
        isExcludeFromSequence: true,
        showCount: {
            fieldId: "TotalCount",
            hideOnNavigate: false
        },
        extPageId: conditionCornondelExternalID
    },
    { label: "ContactSupport", displayName: "Contact Support", icon: HelpCircle, extPageId: CorrespondentNonDelegateContact, isExcludeFromSequence: true },
    {
        label: "Request Guideline Exception",
        displayName: "Request Guideline Exception",
        icon: TicketSlashIcon,
        extPageId: CorrespondentNonDelegateGuidelineException,
        fieldId: "LOG.MS.DATE.SUBMITTED TO UW",
        showIf: "isGuidelineExceptionVisible",
        isExcludeFromSequence: true
    },
    {
        label: "Purchase Advice",
        displayName: "Purchase Advice",
        icon: Newspaper,
        isExcludeFromSequence: true,
        showIf: "purchaseAdviceIsVisible",
        requiredPermission: PERMISSIONS.VIEW_PURCHASE_ADVICE
    }
];

const isEmptyField = value => {
    const val = (value || "").trim();
    return val === "" || val === "//" || val === "  /  /    ";
};

export const evaluateMenuCondition = (conditionName, loanFields, extraContext = {}) => {
    const x90 = loanFields["TPO.X90"];
    const x4 = loanFields["TPO.X4"];
    const submittedToUWDate = loanFields["LOG.MS.DATE.SUBMITTED TO UW"];
    const delInPurchaseReview = loanFields["Log.MS.Date.In Purchase Review"];
    const setupCompleted = loanFields["Log.MS.Date.Setup"];
    const docsOutCompleted = loanFields["Log.MS.Date.Docs Out"];
    const brokerOptions = extraContext.brokerOptions;
    const purchasedVisible = loanFields["Log.MS.Date.Purchased"];
    const nonDelegatedUWFiles = loanFields["LOG.MS.DATE.SUBMITTED TO UW"];
    const nonDelegatedClosedFiles = loanFields["LOG.MS.DATE.SUBMITTED TO UW"];
    const delegatedConditions = loanFields["LOG.MS.DATE.CLOSED LOAN SUBMITTED"];
    const enableIsRegistrationCompleted = loanFields["1401"];
    const LoanCancelFolder = process.env.REACT_APP_WHL_LOAN_CANCEL_FOLDER ?? "";
    const isDisClosureRequested = brokerOptions === "RequestLEAndDisclosures" ? !isEmptyField(loanFields["TPO.X92"]) : !shouldShowViewDisclosures(loanFields);

    switch (conditionName) {
        case "isInitialSubmit":
            return (brokerOptions === "DisableFeeManagement" || brokerOptions === "RequestLEAndDisclosures" || brokerOptions === "GenerateLEAndDisclosures") && isEmptyField(x90);
        case "isInitialSubmitBP":
            return (brokerOptions === "DisableFeeManagement" || brokerOptions === "RequestLEAndDisclosures" || brokerOptions === "GenerateLEAndDisclosures") && isEmptyField(x90);
        case "isReSubmit":
            return !isEmptyField(x90) && isEmptyField(x4);
        case "isGuidelineExceptionVisible":
            return isEmptyField(submittedToUWDate);
        case "isDelGuidelineExceptionVisible":
            return isEmptyField(delInPurchaseReview);
        case "showSendDisclosures":
            return !!extraContext.showSendDisclosures;
        case "isRequestLE":
            return !!extraContext.showRequestLE;
        case "isDisclosure":
            return !!extraContext.showDisclosures;
        case "isChangeOfCircumstance":
            return isCOCVisible && !isEmptyField(x90) && isEmptyField(docsOutCompleted);
        case "isSubmittoUW":
            return !isEmptyField(submittedToUWDate);
        case "purchaseAdviceIsVisible":
            return !isEmptyField(purchasedVisible);
        case "isNonDelegatedUWFiles":
            return isEmptyField(nonDelegatedUWFiles) || isEmptyField(x90);
        case "isNonDelegatedClosedFiles":
            return !isEmptyField(nonDelegatedClosedFiles);
        case "isDelegateConditionsVisible":
            return !isEmptyField(delegatedConditions);
        case "isRegistrationCompleted":
            return !isEmptyField(enableIsRegistrationCompleted);
        case "isWsConditionsBetaVisible":
            return isBetaConditionVsisible && !isEmptyField(enableIsRegistrationCompleted);
        case "isCorDelConditionsBetaVisible":
            return isBetaConditionVsisible && !isEmptyField(delegatedConditions);
        case "isCorNonDelConditionsBetaVisible":
            return isBetaConditionVsisible && !isEmptyField(nonDelegatedClosedFiles);
        case "isWsDocumentsBetaVisible":
            return isBetaDocumentsVisible && !isEmptyField(enableIsRegistrationCompleted);
        case "isCorDelDocumentsBetaVisible":
            return isBetaDocumentsVisible && !isEmptyField(enableIsRegistrationCompleted) && isEmptyField(delegatedConditions);
        case "isCorNonDelDocumentsBetaVisible":
            return isBetaDocumentsVisible && !isEmptyField(enableIsRegistrationCompleted);
        case "isAiDocumentReviewVisible": {
            if (!isBetaConditionVsisible) {
                console.log("Condition Beta is disabled. Upload features will be hidden.");
                return false;
            }

            const indexResponsibility = (loanFields["CX.COND.INDEXRESPONSIBILITY"] || "")
                .toString()
                .trim()
                .toLowerCase();

            // Show only for Broker using AI.
            const compact = indexResponsibility.replace(/[^a-z0-9]/g, "");
            const isBrokerOnly = compact === "brokeronly";
            const isInternalUsingAi = compact === "internalusingai";
            const isBrokerUsingAi = compact === "brokerusingai";

            return isBrokerUsingAi && !isBrokerOnly && !isInternalUsingAi && !isEmptyField(enableIsRegistrationCompleted);
        }

        case "isPricingBetaMenuVisible":
            return isPricingBetaMenuVisible && !isEmptyField(pricingBetaExternalPageID);
        case "isCancelLoan":
            return !isEmptyField(LoanCancelFolder) && isDisClosureRequested;
        case "isAppraisalOrderInfoVisible":
            return process.env.REACT_APP_APPRAISAL_ORDER_INFO_EXTERNAL !== "TBD";
        case "isRegistrationCompletedAndNotSubmittedtoUW":
            return !isEmptyField(enableIsRegistrationCompleted) && isEmptyField(submittedToUWDate);
        case "isRegistrationCompletedAndNotClosedLoanSubmitted":
            return !isEmptyField(enableIsRegistrationCompleted) && isEmptyField(delegatedConditions);
        default:
            return true;
    }
};

export const loanFieldIdsToRead = [
    "CX.LOAN.TYPE",
    "1811",
    "3238",
    "CX.BROK.UNREADMSG.COUNT",
    "CX.COND.INDEXRESPONSIBILITY",
    "LOG.MS.DATE.SUBMITTED TO UW",
    "TPO.X15",
    "Log.MS.Date.Docs Out",
    "Log.MS.Date.Setup",
    "4460", // Number of Borrower Pairs
    "300",
    "300#1",
    "300#2",
    "300#3",
    "300#4",
    "300#5",
    "1401",
    "TPO.X92",
    "1041",
    "TPO.X90",
    "TPO.X115",
    "CX.LOAN.PROD.BROKER.SELECT",
    "CX.DOC.TYPE",
    "1548",
    "LCP.X1",
    "CX.CITIZENSHIP.TYPE",
    "1041",
    "16",
    "CX.INCOME.TYPE",
    "URLA.X73",
    "12",
    "14",
    "15",
    "1821",
    "1041",
    "1811",
    "608",
    "4494",
    "1109",
    "4000",
    "4002",
    "65",
    "1402",
    "URLA.X1",
    "52",
    "1490",
    "1240",
    "BR0126",
    "BR0106",
    "BR0107",
    "BR0108",
    "BR0112",
    "BR0124",
    "FR0126",
    "FR0106",
    "FR0107",
    "FR0108",
    "389",
    "1041",
    "CX.COC.CHANGEDATE",
    "641", //Smart fees by auto ordered after pricing
    "2353",
    "2352", //Order appraisal - Appraisal ordered and completed
    "LOANFILESEQUENCENUMBER",
    "19",
    "DD0108",
    "DD0208",
    "DD0308",
    "DD0408",
    "DD0508",
    "DD0608",
    "DD0708",
    "DD0808",
    "DD0908",
    "DD1008",
    "3152",
    "2626",
    "TPO.X88",
    "Log.MS.Date.Purchased",
    "Log.MS.Date.In Purchase Review", //Channel
    "CX.ALL.DISCLOSURES.SENT",
    "LOG.MS.DATE.INITIAL DECISION",
    "LOG.MS.DATE.CLOSED LOAN SUBMITTED"
];

const shouldShowViewDisclosures = loanFields => {
    const cxAll = (loanFields?.["CX.ALL.DISCLOSURES.SENT"] ?? "").toString().trim().toUpperCase();
    const x90 = loanFields?.["TPO.X90"];

    const allDisclosuresSent = cxAll === "Y";
    const submittedAlready = !isEmptyField(x90);

    return allDisclosuresSent || (cxAll === "" && submittedAlready);
};